import { LinkOutlined } from '@ant-design/icons';
import { getOrderShortCode } from '@mahawi/eshop-common';
import { getTranslation } from '@mahawi/eshop-common/src/translation';
import {
  type ILanguageType,
  type IWarehouseDetail,
  type IWarehouseOrderUnpairedProduct,
  type IWarehouseOrderUnpairedProductFilters,
  type IWarehouseOrderUnpairedProductSuggestion,
} from '@mahawi/eshop-common/src/types';
import { Button, Segmented, Select, Space, Typography } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';

import DateRangeFilter from '../components/date-range-filter';
import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import TableComponent from '../components/table';
import { formatAmount } from '../components/warehouse-order/labels';
import { EDateFormat } from '../fragments/date-format';
import {
  EDateRangePreset,
  fromInclusiveDates,
  type IDateRange,
  resolveDateRange,
  toInclusiveDates,
  toIsoDateRange,
} from '../fragments/date-range';
import { EMPTY_VALUE } from '../fragments/empty-value';
import { parseList } from '../fragments/parse-list';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import {
  warehouseOrderUnpairedProductPair,
  warehouseOrderUnpairedProductsLoad,
} from '../reducers/warehouse-order/reducer';
import { SPACING } from '../theme/tokens';

const { Text } = Typography;

const DEFAULT_PAGE_SIZE = 50;

enum EUnpairedProductsSearchParam {
  FROM = 'from',
  TO = 'to',
  WAREHOUSE = 'warehouse',
  VIEW = 'view',
}

export enum EUnpairedProductsView {
  WITH_SUGGESTIONS = 'withSuggestions',
  ALL = 'all',
}

const VIEW_OPTIONS: { value: EUnpairedProductsView; label: string }[] = [
  { value: EUnpairedProductsView.WITH_SUGGESTIONS, label: 'With suggestions' },
  { value: EUnpairedProductsView.ALL, label: 'All' },
];

type TUnpairedProductRow = IWarehouseOrderUnpairedProduct & { key: string };

const getColumns = ({
  languageType,
  isPairDisabled,
  onPair,
}: {
  languageType: ILanguageType | undefined;
  isPairDisabled: boolean;
  onPair: (warehouseOrderUuid: string, orderUuid: string) => void;
}): ColumnsType<TUnpairedProductRow> => [
  {
    title: 'Order',
    render: (
      _value: string,
      record: TUnpairedProductRow,
    ): React.ReactElement => (
      <Link
        to={`/order/${record.order.uuid}`}
        label={getOrderShortCode(record.order.uuid)}
        openInNewTab
      />
    ),
  },
  {
    title: 'Created',
    render: (_value: string, record: TUnpairedProductRow): string =>
      dayjs(record.order.createdAt).format(EDateFormat.DATE),
    sorter: (a: TUnpairedProductRow, b: TUnpairedProductRow): number =>
      dayjs(a.order.createdAt).unix() - dayjs(b.order.createdAt).unix(),
  },
  {
    title: 'Status',
    render: (_value: string, record: TUnpairedProductRow): string =>
      record.order.orderStatus,
  },
  {
    title: 'Product',
    render: (
      _value: string,
      record: TUnpairedProductRow,
    ): React.ReactElement => (
      <Link
        to={`/product/${record.product.uuid}`}
        label={
          getTranslation(record.product.names, languageType) ?? EMPTY_VALUE
        }
        openInNewTab
      />
    ),
  },
  {
    title: 'Pieces without warehouse order',
    align: 'right',
    render: (_value: string, record: TUnpairedProductRow): string =>
      `${record.uncoveredQuantity} / ${record.orderedQuantity}`,
    sorter: (a: TUnpairedProductRow, b: TUnpairedProductRow): number =>
      a.uncoveredQuantity - b.uncoveredQuantity,
  },
  {
    title: 'Suggested warehouse orders',
    render: (_value: string, record: TUnpairedProductRow): React.ReactNode => {
      if (record.suggestions.length === 0) {
        return (
          <Text type="secondary">No warehouse order with free pieces</Text>
        );
      }

      return (
        <Space orientation="vertical" size={SPACING.xs}>
          {record.suggestions.map(
            (
              suggestion: IWarehouseOrderUnpairedProductSuggestion,
            ): React.ReactElement => (
              <Space key={suggestion.uuid} size={SPACING.sm} wrap>
                <Link
                  to={`/orders/warehouse-order/${suggestion.uuid}`}
                  label={`${suggestion.warehouse.name} ${suggestion.code}`}
                  openInNewTab
                />
                <Text type="secondary">
                  {dayjs(suggestion.orderedAt).format(EDateFormat.DATE)}
                </Text>
                <Text>{suggestion.freeQuantity} free</Text>
                <Text type="secondary">
                  {formatAmount(
                    suggestion.unitPrice,
                    suggestion.currencyIsoCode,
                  )}
                </Text>
                <Button
                  size="small"
                  icon={<LinkOutlined />}
                  disabled={isPairDisabled}
                  onClick={(): void => {
                    onPair(suggestion.uuid, record.order.uuid);
                  }}
                >
                  Pair
                </Button>
              </Space>
            ),
          )}
        </Space>
      );
    },
  },
];

function WarehouseOrderUnpairedProductsContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const unpairedProducts = useSelector(
    (s: RootState) => s.WarehouseOrder.unpairedProducts,
  );
  const inProcess = useSelector((s: RootState) => s.WarehouseOrder.inProcess);
  const warehouses = useSelector((s: RootState) => s.Warehouse.warehouses);
  const languageType = useSelector((s: RootState) => s.Language.languageType);
  const [searchParams, setSearchParams] = useSearchParams();

  const from: string | null = searchParams.get(
    EUnpairedProductsSearchParam.FROM,
  );
  const to: string | null = searchParams.get(EUnpairedProductsSearchParam.TO);
  const range: IDateRange =
    from && to
      ? fromInclusiveDates(from, to)
      : resolveDateRange(EDateRangePreset.LAST_30_DAYS);
  const view: EUnpairedProductsView =
    searchParams.get(EUnpairedProductsSearchParam.VIEW) ===
    EUnpairedProductsView.ALL
      ? EUnpairedProductsView.ALL
      : EUnpairedProductsView.WITH_SUGGESTIONS;
  const filters: IWarehouseOrderUnpairedProductFilters = {
    ...toIsoDateRange(range),
    warehouseUuids: parseList(
      searchParams.get(EUnpairedProductsSearchParam.WAREHOUSE),
    ),
  };
  const filtersKey: string = JSON.stringify(filters);

  const load = (): void => {
    dispatch(warehouseOrderUnpairedProductsLoad(filters));
  };

  useEffect(load, [dispatch, filtersKey]);

  const setParam = (key: EUnpairedProductsSearchParam, value: string): void => {
    if (value) {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }

    searchParams.delete('page');
    setSearchParams(searchParams);
  };

  const rows = useMemo<TUnpairedProductRow[]>(
    () =>
      (unpairedProducts ?? [])
        .filter(
          (unpairedProduct: IWarehouseOrderUnpairedProduct): boolean =>
            view === EUnpairedProductsView.ALL ||
            unpairedProduct.suggestions.length > 0,
        )
        .map(
          (
            unpairedProduct: IWarehouseOrderUnpairedProduct,
          ): TUnpairedProductRow => ({
            key: `${unpairedProduct.order.uuid}-${unpairedProduct.product.uuid}`,
            ...unpairedProduct,
          }),
        ),
    [unpairedProducts, view],
  );

  return (
    <Space size={SPACING.lg} orientation="vertical" style={{ width: '100%' }}>
      <DetailPageHeader
        title="Unpaired products"
        onReload={load}
        isReloadDisabled={inProcess}
        actions={
          <>
            <DateRangeFilter
              value={range}
              disabled={inProcess}
              onChange={(nextRange: IDateRange): void => {
                const [nextFrom, nextTo]: [string, string] =
                  toInclusiveDates(nextRange);

                searchParams.set(EUnpairedProductsSearchParam.FROM, nextFrom);
                searchParams.set(EUnpairedProductsSearchParam.TO, nextTo);
                searchParams.delete('page');
                setSearchParams(searchParams);
              }}
            />
            <Select
              mode="multiple"
              allowClear
              placeholder="Warehouse"
              style={{ minWidth: 200 }}
              options={(warehouses ?? [])
                .filter(
                  (warehouse: IWarehouseDetail): boolean =>
                    !warehouse.isInternal,
                )
                .map((warehouse: IWarehouseDetail) => ({
                  value: warehouse.uuid,
                  label: warehouse.name,
                }))}
              value={filters.warehouseUuids}
              onChange={(values: string[]): void => {
                setParam(
                  EUnpairedProductsSearchParam.WAREHOUSE,
                  values.join(','),
                );
              }}
            />
            <Segmented<EUnpairedProductsView>
              options={VIEW_OPTIONS}
              value={view}
              onChange={(value: EUnpairedProductsView): void => {
                setParam(
                  EUnpairedProductsSearchParam.VIEW,
                  value === EUnpairedProductsView.ALL ? value : '',
                );
              }}
            />
          </>
        }
        info={
          unpairedProducts && (
            <Text type="secondary">
              {rows.length} of {unpairedProducts.length} products
            </Text>
          )
        }
      />

      <TableComponent
        columns={
          getColumns({
            languageType,
            isPairDisabled: inProcess,
            onPair: (warehouseOrderUuid: string, orderUuid: string): void => {
              dispatch(
                warehouseOrderUnpairedProductPair({
                  warehouseOrderUuid,
                  orderUuid,
                  filters,
                }),
              );
            },
          }) as Parameters<typeof TableComponent>[0]['columns']
        }
        dataSource={rows}
        isLoading={inProcess}
        size="small"
        scroll={{ x: 'max-content' }}
        defaultPageSize={DEFAULT_PAGE_SIZE}
        emptyText="No unpaired products in this period"
      />
    </Space>
  );
}

export default WarehouseOrderUnpairedProductsContainer;
